import type { LucideIcon } from "lucide-react";
import { Loader2 } from "lucide-react";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "./cn";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "subtle"
  | "success"
  | "danger"
  | "link";

export type ButtonSize = "xs" | "sm" | "md" | "lg";

const BASE =
  "inline-flex items-center justify-center gap-2 font-medium whitespace-nowrap rounded-md " +
  "transition-colors duration-150 select-none " +
  "disabled:opacity-50 disabled:pointer-events-none";

const VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-accent text-accent-contrast hover:bg-accent-hover shadow-e1",
  secondary:
    "bg-surface text-text border border-line hover:bg-surface-hover shadow-e1",
  ghost: "text-muted hover:bg-surface-hover hover:text-text",
  subtle: "bg-surface-sunken text-text hover:bg-surface-hover",
  // The affirmative counterpart to `danger`, for an action whose whole meaning
  // is "yes" — approving a week. Same solid treatment, same contrast promise.
  success: "bg-success-solid text-white hover:opacity-90 shadow-e1",
  danger: "bg-danger-solid text-white hover:opacity-90 shadow-e1",
  link: "text-accent hover:underline underline-offset-2 h-auto p-0",
};

const SIZE: Record<ButtonSize, string> = {
  xs: "h-7 px-2 text-xs",
  sm: "h-8 px-2.5 text-sm",
  md: "h-9 px-3 text-sm",
  lg: "h-11 px-4 text-base",
};

/** Square, for icon-only buttons. `lg` is 44px — the touch-target floor. */
const ICON_SIZE: Record<ButtonSize, string> = {
  xs: "h-7 w-7 p-0",
  sm: "h-8 w-8 p-0",
  md: "h-9 w-9 p-0",
  lg: "h-11 w-11 p-0",
};

export function buttonClass({
  variant = "primary",
  size = "md",
  iconOnly = false,
  fullWidth = false,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconOnly?: boolean;
  fullWidth?: boolean;
} = {}): string {
  return cn(
    BASE,
    VARIANT[variant],
    variant === "link" ? null : iconOnly ? ICON_SIZE[size] : SIZE[size],
    fullWidth && "w-full"
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon. Swapped for a spinner while `loading`. */
  icon?: LucideIcon;
  /** Trailing icon. Never replaced — it stays put so the button doesn't resize. */
  trailingIcon?: LucideIcon;
  iconOnly?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
}

/**
 * The button.
 *
 * There were five hand-rolled copies of "primary button" in the app and they had
 * already drifted — the dashboard's was missing `disabled:opacity-50`, so it
 * looked clickable while disabled.
 *
 * On `loading`: the label stays rendered and only the *icon* becomes a spinner.
 * Swapping the text to "Saving…" would change the button's accessible name
 * mid-flight, which breaks anything holding a reference to it — including five
 * e2e specs that find controls by name — and moves the target under a user's
 * cursor. `aria-busy` carries the state instead.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    icon: Icon,
    trailingIcon: TrailingIcon,
    iconOnly = false,
    fullWidth = false,
    loading = false,
    disabled,
    className = "",
    children,
    type = "button",
    ...rest
  },
  ref
) {
  const glyph = "h-4 w-4 shrink-0";
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonClass({ variant, size, iconOnly, fullWidth }), className)}
      {...rest}
    >
      {loading ? (
        <Loader2 className={cn(glyph, "animate-spin")} aria-hidden="true" />
      ) : Icon ? (
        <Icon className={glyph} aria-hidden="true" />
      ) : null}
      {children}
      {TrailingIcon && !iconOnly && (
        <TrailingIcon className={glyph} aria-hidden="true" />
      )}
    </button>
  );
});
